import { firestoreDb, firebaseAuth } from './firebase';
import {
    doc,
    setDoc,
    onSnapshot,
    serverTimestamp,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import PubSub, { topics } from './lib/pubsub';

class FirebaseSync {
    constructor() {
        this.unsubscribe = null;
        this.authUnsubscribe = null;
        this.customerId = null;
        this.userId = null;
        this.userName = '';
        this.sessionId = Math.random().toString(36).substring(2) + Date.now().toString(36);
        this.isInitialSnapshot = true;
        this.lastSaleTimestamp = null;
        this.lastJournalTimestamp = null;
    }

    start({ customerId, userId, userName }) {
        console.log('[FirebaseSync] start() called', { customerId, userId, userName });
        this.stop();

        if (!customerId || !userId) {
            console.log('[FirebaseSync] start() aborted - missing customerId or userId');
            return;
        }

        this.customerId = customerId;
        this.userId = userId;
        this.userName = userName;
        this.isInitialSnapshot = true;

        // Wait for Firebase Auth to be ready before setting up Firestore listener
        const currentUser = firebaseAuth.currentUser;
        if (currentUser) {
            console.log('[FirebaseSync] Firebase Auth already signed in, starting listener now');
            this.startListener();
        } else {
            console.log('[FirebaseSync] Firebase Auth not ready, waiting for onAuthStateChanged...');
            this.authUnsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
                if (user) {
                    console.log('[FirebaseSync] Firebase Auth ready (uid:', user.uid, '), starting listener');
                    if (this.authUnsubscribe) {
                        this.authUnsubscribe();
                        this.authUnsubscribe = null;
                    }
                    this.startListener();
                } else {
                    console.log('[FirebaseSync] onAuthStateChanged fired but no user yet');
                }
            });
        }
    }

    startListener() {
        try {
            const docPath = `sync/${String(this.customerId)}`;
            console.log('[FirebaseSync] Setting up onSnapshot listener for:', docPath);
            const docRef = doc(firestoreDb, 'sync', String(this.customerId));
            this.unsubscribe = onSnapshot(docRef, (snapshot) => {
                console.log('[FirebaseSync] onSnapshot fired, exists:', snapshot.exists(), 'data:', snapshot.exists() ? snapshot.data() : null);
                this.handleSnapshot(snapshot);
            }, (error) => {
                console.warn('[FirebaseSync] onSnapshot error:', error.code, error.message);
            });
            console.log('[FirebaseSync] Listener set up successfully');
        } catch (err) {
            console.warn('[FirebaseSync] Failed to start listener:', err.message);
        }
    }

    handleSnapshot(snapshot) {
        const data = snapshot.exists() ? snapshot.data() : {};

        if (this.isInitialSnapshot) {
            console.log('[FirebaseSync] Initial snapshot - recording timestamps, no banner');
            this.isInitialSnapshot = false;
            this.lastSaleTimestamp = data.saleUpdated || null;
            this.lastJournalTimestamp = data.journalUpdated || null;
            console.log('[FirebaseSync] Initial timestamps:', {
                lastSaleTimestamp: this.lastSaleTimestamp,
                lastJournalTimestamp: this.lastJournalTimestamp,
            });
            return;
        }

        console.log('[FirebaseSync] Subsequent snapshot - checking for changes');
        console.log('[FirebaseSync] data.saleSessionId:', data.saleSessionId, 'this.sessionId:', this.sessionId, 'same?', data.saleSessionId === this.sessionId);
        console.log('[FirebaseSync] data.journalSessionId:', data.journalSessionId, 'this.sessionId:', this.sessionId, 'same?', data.journalSessionId === this.sessionId);

        // Check sale updates (use sessionId to allow same user in different tabs)
        if (data.saleUpdated && data.saleSessionId !== this.sessionId) {
            const newTimestamp = data.saleUpdated;
            const timestampChanged = !this.lastSaleTimestamp || newTimestamp.seconds !== this.lastSaleTimestamp.seconds;
            console.log('[FirebaseSync] Sale update detected - timestampChanged:', timestampChanged, 'new:', newTimestamp?.seconds, 'old:', this.lastSaleTimestamp?.seconds);
            if (timestampChanged) {
                this.lastSaleTimestamp = newTimestamp;
                console.log('[FirebaseSync] Publishing SYNC_SALE_UPDATED, updatedByName:', data.saleUpdatedByName, 'saleId:', data.saleUpdatedId);
                PubSub.publish(topics.SYNC_SALE_UPDATED, {
                    updatedByName: data.saleUpdatedByName || '',
                    saleId: data.saleUpdatedId || null,
                });
            }
        }

        // Check journal updates (use sessionId to allow same user in different tabs)
        if (data.journalUpdated && data.journalSessionId !== this.sessionId) {
            const newTimestamp = data.journalUpdated;
            const timestampChanged = !this.lastJournalTimestamp || newTimestamp.seconds !== this.lastJournalTimestamp.seconds;
            console.log('[FirebaseSync] Journal update detected - timestampChanged:', timestampChanged, 'new:', newTimestamp?.seconds, 'old:', this.lastJournalTimestamp?.seconds);
            if (timestampChanged) {
                this.lastJournalTimestamp = newTimestamp;
                console.log('[FirebaseSync] Publishing SYNC_JOURNAL_UPDATED, updatedByName:', data.journalUpdatedByName, 'journalId:', data.journalUpdatedId);
                PubSub.publish(topics.SYNC_JOURNAL_UPDATED, {
                    updatedByName: data.journalUpdatedByName || '',
                    journalId: data.journalUpdatedId || null,
                });
            }
        }
    }

    broadcastSaleUpdate(saleId) {
        console.log('[FirebaseSync] broadcastSaleUpdate() called', { saleId, customerId: this.customerId, userId: this.userId, userName: this.userName });
        if (!this.customerId || !this.userId) {
            console.log('[FirebaseSync] broadcastSaleUpdate() aborted - not initialized');
            return;
        }
        try {
            const docRef = doc(firestoreDb, 'sync', String(this.customerId));
            console.log('[FirebaseSync] Writing sale update to Firestore...');
            setDoc(docRef, {
                saleUpdated: serverTimestamp(),
                saleUpdatedBy: this.userId,
                saleUpdatedByName: this.userName,
                saleSessionId: this.sessionId,
                saleUpdatedId: saleId || null,
            }, { merge: true }).then(() => {
                console.log('[FirebaseSync] Sale update written successfully');
            }).catch((err) => {
                console.error('[FirebaseSync] Sale update write failed:', err.code, err.message);
            });
        } catch (err) {
            console.warn('[FirebaseSync] Failed to broadcast sale update:', err.message);
        }
    }

    broadcastJournalUpdate(journalId) {
        console.log('[FirebaseSync] broadcastJournalUpdate() called', { journalId, customerId: this.customerId, userId: this.userId, userName: this.userName });
        if (!this.customerId || !this.userId) {
            console.log('[FirebaseSync] broadcastJournalUpdate() aborted - not initialized');
            return;
        }
        try {
            const docRef = doc(firestoreDb, 'sync', String(this.customerId));
            console.log('[FirebaseSync] Writing journal update to Firestore...');
            setDoc(docRef, {
                journalUpdated: serverTimestamp(),
                journalUpdatedBy: this.userId,
                journalUpdatedByName: this.userName,
                journalSessionId: this.sessionId,
                journalUpdatedId: journalId || null,
            }, { merge: true }).then(() => {
                console.log('[FirebaseSync] Journal update written successfully');
            }).catch((err) => {
                console.error('[FirebaseSync] Journal update write failed:', err.code, err.message);
            });
        } catch (err) {
            console.warn('[FirebaseSync] Failed to broadcast journal update:', err.message);
        }
    }

    stop() {
        console.log('[FirebaseSync] stop() called');
        if (this.authUnsubscribe) {
            this.authUnsubscribe();
            this.authUnsubscribe = null;
        }
        if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
        }
        this.customerId = null;
        this.userId = null;
        this.userName = '';
        this.isInitialSnapshot = true;
        this.lastSaleTimestamp = null;
        this.lastJournalTimestamp = null;
    }
}

export default new FirebaseSync();
